import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import VideoProcessingStatus from './VideoProcessingStatus';

export interface ProcessingOptions {
  generateTranscript: boolean;
  detectSpeakers: boolean;
  extractObjects: boolean;
}

interface UploadCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string | null;
  videoName: string;
  onProcessNow: (options: ProcessingOptions) => void;
  onProcessLater: () => void;
}

export default function UploadCompletionModal({
  isOpen,
  onClose,
  videoId,
  videoName,
  onProcessNow,
  onProcessLater,
}: UploadCompletionModalProps) {
  const [options, setOptions] = React.useState<ProcessingOptions>({
    generateTranscript: true,
    detectSpeakers: true,
    extractObjects: true,
  });
  const [processingStarted, setProcessingStarted] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');

  const handleOptionChange = (key: keyof ProcessingOptions) => (checked: boolean) => {
    setOptions(prev => ({ ...prev, [key]: checked }));
  };

  const handleProcessNow = async () => {
    setProcessingStarted(true);
    await onProcessNow(options);
    // Don't close modal immediately - let user see processing status
  };

  const handleProcessLater = () => {
    onProcessLater();
    onClose();
  };

  const handleClose = () => {
    if (processingStarted && processingStatus === 'COMPLETED') {
      // If processing is complete, allow closing
      onClose();
    } else if (processingStarted) {
      // If still processing, show confirmation
      if (window.confirm('Processing is still in progress. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProcessingStarted(false);
      setProcessingStatus('');
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {processingStarted ? 'Video Processing' : 'Video Upload Complete'}
          </DialogTitle>
          <DialogDescription>
            {processingStarted 
              ? `Processing "${videoName}"...`
              : `Your video "${videoName}" has been uploaded successfully. Choose how you'd like to process it.`
            }
          </DialogDescription>
        </DialogHeader>
        
        {!processingStarted ? (
          <>
            <div className="grid gap-4 py-4">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="transcript"
                    checked={options.generateTranscript}
                    onCheckedChange={handleOptionChange('generateTranscript')}
                  />
                  <Label htmlFor="transcript">Generate transcript</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="speakers"
                    checked={options.detectSpeakers}
                    onCheckedChange={handleOptionChange('detectSpeakers')}
                  />
                  <Label htmlFor="speakers">Detect speakers</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="objects"
                    checked={options.extractObjects}
                    onCheckedChange={handleOptionChange('extractObjects')}
                  />
                  <Label htmlFor="objects">Extract objects and scenes</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={handleProcessLater}
                className="sm:w-auto w-full"
              >
                Process Later
              </Button>
              <Button
                onClick={handleProcessNow}
                className="sm:w-auto w-full"
              >
                Process Now
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-4">
            {videoId && (
              <VideoProcessingStatus 
                videoId={videoId}
                onStatusChange={(status) => {
                  setProcessingStatus(status);
                  if (status === 'COMPLETED') {
                    // Auto-close after a delay when completed
                    setTimeout(() => {
                      onClose();
                    }, 3000);
                  }
                }}
              />
            )}
            
            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="w-full"
              >
                {processingStatus === 'COMPLETED' ? 'Close' : 'Close Anyway'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
